'use client'

import React, { FC, ReactElement } from 'react'
import { friendlyTxError } from '@/utils/friendly-tx-error'
import Link from 'next/link'
import classNames from 'classnames'
import { CirclePlus, CircleCheck, ChevronDown, Clock } from 'lucide-react'
import { Slider } from '@/components/ui/slider'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import formatNumber from '@/utils/format-number'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import { API_URL, EXPLORER_URL, NETWORK } from '@/utils/constants'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { useToast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import BitcoinIcon from '@/icons/bitcoin-filled'
import EditIcon from '@/icons/edit'
import Invoice from '../../components/invoice'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import { planSplit, seedReserve } from './plan-split'
import { recordSplitPrompt, takeSplitPromptSource, thresholdSats } from '@/utils/split-prompt'
import {
	beginWalletFlow,
	classifyWalletError,
	errorDetail,
	finishWalletFlow,
	providerSlug,
	telemetryPath
} from '@/utils/wallet/telemetry'

// Set to true to use the old UtxosCart modal UI instead of the new Invoice-based view
const USE_CLASSIC = false

const UtxoManager: FC<{
	onNavigate: (v: string) => void
	onClose: () => void
	args?: any
}> = (props): ReactElement => {
	const { onClose } = props
	// `args.embedded`: rendered inside another surface (the wallet's split
	// prompt) that already explains splitting; drop the intro, add padding.
	const embedded = Boolean(props.args?.embedded)

	if (USE_CLASSIC) {
		const UtxosCart = require('@/app/wallet/wallet/wallet-utxos/utxos-cart').default
		return (
			<div className="flex flex-col items-center">
				<UtxosCart handleModalClose={onClose} />
			</div>
		)
	}

	return <UtxoManagerNew onClose={onClose} embedded={embedded} />
}

const UtxoManagerNew: FC<{
	onClose: () => void
	embedded?: boolean
}> = ({ onClose, embedded = false }): ReactElement => {
	const { toast } = useToast()
	const { exchangeRate } = useExchangeRate()
	const [step, setStep] = React.useState<1 | 2>(1)

	const { data, isLoading } = useSwr(
		`${API_URL}/wallet/${Wallet.balanceAddress()}/all-utxos`,
		fetcher
	)

	const [sortOrder, setSortOrder] = React.useState<'biggest' | 'smallest'>('biggest')
	const [sortOpen, setSortOpen] = React.useState(false)
	const sortRef = React.useRef<HTMLDivElement>(null)
	// Start at the most pieces the slider offers: ten UTXOs means ten
	// purchases before anything has to confirm.
	const [numOutputs, setNumOutputs] = React.useState(10)
	// Splits always return to the payment address; a custom destination was
	// an easy way to lose track of where the pieces went.
	const sendToAddress = ''
	const [selected, setSelected] = React.useState(new Set<string>())
	const [slider, setSlider] = React.useState(0)
	const [computedServiceFee, setComputedServiceFee] = React.useState(0)
	const [computedOutputValue, setComputedOutputValue] = React.useState(0)

	// Confirmed coins are selectable; coins still in the mempool (change
	// from a purchase or send that has not confirmed) are listed too, marked
	// Pending, so the customer sees where their balance is instead of a
	// node error when the split collides with that transaction.
	const utxos = React.useMemo(() => {
		const confirmed = (data?.utxos || []).map((e: any) => ({ ...e, pending: false }))
		const pending = (data?.mempool || []).map((e: any) => ({ ...e, pending: true }))
		const all = [...confirmed, ...pending]
			.sort((a: any, b: any) => (sortOrder === 'biggest' ? b.value - a.value : a.value - b.value))
			.map((e: any) => ({ ...e, outpoint: `${e.txid}:${e.vout}` }))
		if (!embedded) return all
		// In the wallet's split prompt, list only the UTXOs worth splitting
		// (the prompt's own $100 line). A tester or test-link wallet with none
		// that large still gets its single largest, so the flow can be tried.
		const minSats = thresholdSats(exchangeRate)
		const large = all.filter((e: any) => !e.pending && e.value >= minSats)
		if (large.length > 0) return large
		const biggest = all
			.filter((e: any) => !e.pending)
			.reduce((top: any, e: any) => (!top || e.value > top.value ? e : top), null)
		return biggest ? [biggest] : []
	}, [data, sortOrder, embedded, exchangeRate])

	const { selectedUtxos, inputSats } = React.useMemo(() => {
		const selectedUtxos = Array.from(selected.values()).flatMap((e) => {
			const u = utxos.find((u: any) => u.outpoint === e)
			return u ? [u] : []
		})
		const inputSats = selectedUtxos.reduce((a, e) => e.value + a, 0)
		return { selectedUtxos, inputSats }
	}, [selected, utxos])

	const handleSelect = React.useCallback(
		(outpoint: string) => {
			if (utxos.find((u: any) => u.outpoint === outpoint)?.pending) return
			const newSet = new Set(selected)
			if (newSet.has(outpoint)) {
				newSet.delete(outpoint)
			} else {
				newSet.add(outpoint)
			}
			setSelected(newSet)
			setSlider(newSet.size)
		},
		[selected, utxos]
	)

	const onSliderChange = React.useCallback(
		([v]: any) => {
			setSlider(v)
			const newSet = new Set<string>()
			for (let i = 0; i < v && i < utxos.length; i++) {
				newSet.add(utxos[i].outpoint)
			}
			setSelected(newSet)
		},
		[utxos]
	)

	const handleSortChange = React.useCallback((order: 'biggest' | 'smallest') => {
		setSortOrder(order)
		setSortOpen(false)
	}, [])

	// Close sort dropdown on outside click
	React.useEffect(() => {
		if (!sortOpen) return
		const handler = (e: MouseEvent) => {
			if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
				setSortOpen(false)
			}
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [sortOpen])

	// Re-select from top when sort order changes
	React.useEffect(() => {
		if (slider > 0 && utxos.length > 0) {
			const newSet = new Set<string>()
			for (let i = 0; i < slider && i < utxos.length; i++) {
				newSet.add(utxos[i].outpoint)
			}
			requestAnimationFrame(() => {
				setSelected(newSet)
			})
		}
	}, [slider, sortOrder, utxos])

	// Auto-select first UTXO
	React.useEffect(() => {
		if (selected.size === 0 && utxos.length > 0) {
			const utxo = utxos[0]
			if (utxo) {
				requestAnimationFrame(() => {
					setSelected(new Set([utxo.outpoint]))
					setSlider(1)
				})
			}
		}
	}, [selected.size, utxos])

	const handleOutputsInput = React.useCallback((evt: any) => {
		const val = parseInt(evt.target.value, 10)
		if (isNaN(val) || val < 1) {
			setNumOutputs(1)
		} else {
			setNumOutputs(val)
		}
	}, [])

	const handleBuildPsbt = React.useCallback(
		async (deps: any[], feeRate: number) => {
			const [dSelected, dNumOutputs, dSendToAddress] = deps

			if (dSelected.size === 0) return null

			const selUtxos = Array.from(dSelected.values() as IterableIterator<string>).flatMap((op) => {
				const u = utxos.find((u: any) => u.outpoint === op)
				return u ? [u] : []
			})

			if (!selUtxos.length) return null

			const from = Wallet.balanceAddress()
			const toAddress = dSendToAddress || from
			const dustLimit = NETWORK === 'BTC' ? 546 : 1e6

			// Auto-top-up. BDK treats the `utxos` array as a hard constraint, so
			// if the user's own selection can't cover outputs plus fees the build
			// simply fails. Grow the selection from the unselected pool,
			// biggest-first so we add as few UTXOs as possible.
			const sum = (list: any[]) => list.reduce((a, e) => a + e.value, 0)
			const floorFor = (list: any[]) =>
				dustLimit * dNumOutputs +
				seedReserve({
					inputCount: list.length,
					numOutputs: dNumOutputs,
					toAddress,
					from,
					feeRate,
					dustLimit
				})

			const spend = [...selUtxos]
			if (sum(spend) < floorFor(spend)) {
				const unused = utxos
					.filter((u: any) => !dSelected.has(u.outpoint))
					.sort((a: any, b: any) => b.value - a.value)
				for (const u of unused) {
					if (sum(spend) >= floorFor(spend)) break
					spend.push(u)
				}
				if (spend.length > selUtxos.length) {
					// Reflect the grown selection so the user can see exactly which
					// UTXOs ended up being spent.
					const grown = new Set<string>(spend.map((u) => u.outpoint))
					setSelected(grown)
					setSlider(grown.size)
				}
			}

			const utxoInputs: [string, number, number][] = spend.map((e) => [e.txid, e.vout, e.value])
			const totalValue = sum(spend)

			// All of the fee reasoning lives in planSplit, which derives its
			// numbers from the builder's own responses rather than from
			// assumptions about what the API appends. See plan-split.ts.
			const plan = await planSplit({
				totalValue,
				inputCount: spend.length,
				numOutputs: dNumOutputs,
				toAddress,
				from,
				feeRate,
				dustLimit,
				psbtFee: (psbt) => Number(Wallet.getPSBTFee(psbt)),
				psbtOutputValueTo: (psbt, address) => Wallet.getPSBTOutputValue(psbt, address),
				build: async (outputs) => {
					try {
						const { data } = await axios.post(`${API_URL}/wallet/build`, {
							outputs,
							public_key: Wallet.getPublicKey(),
							from,
							utxos: utxoInputs,
							fee_rate: feeRate,
							// Attributes the 2,500 sat tool fee on the business dashboard.
							purpose: 'utxo_split'
						})
						return { psbt: data.psbt }
					} catch (err: any) {
						// Surface the builder's own wording. That message is how the
						// planner learns the exact deficit when its opening guess is
						// stale, so it must not be swallowed or reworded here.
						const message = err?.response?.data?.message || err?.response?.data || err?.message
						throw new Error(typeof message === 'string' ? message : String(message ?? err))
					}
				}
			})

			setComputedOutputValue(plan.outputValue)
			setComputedServiceFee(plan.serviceFee)

			return { psbt: plan.psbt, fee: BigInt(plan.networkFee) }
		},
		[utxos]
	)

	const handleSubmit = React.useCallback(
		async (psbt: string) => {
			// Telemetry: a split that dies in the wallet or at the node used to
			// leave no trace beyond a built-but-never-broadcast fee row. Report
			// the signing and broadcast outcomes with the wallet and the error.
			const provider = providerSlug(Wallet.display_name() || Wallet.type())
			const context = {
				item_count_bucket: numOutputs <= 1 ? '1' : numOutputs <= 5 ? '2-5' : numOutputs <= 10 ? '6-10' : '11+',
				...(telemetryPath() ? { path: telemetryPath() } : {})
			} as const
			const failure = (error: unknown) => ({
				...context,
				error_category: classifyWalletError(error),
				provider_error_code:
					error && typeof error === 'object' && typeof (error as any).code === 'string'
						? String((error as any).code).slice(0, 32)
						: undefined,
				...(errorDetail(error) ? { error_detail: errorDetail(error) } : {})
			})
			const signFlow = beginWalletFlow('utxo_split', provider, 'utxo_manager', 'sign_psbt', context)
			let rawtx: string
			try {
				rawtx = await Wallet.signPSBTExtractTx(psbt)
			} catch (error) {
				finishWalletFlow(
					signFlow,
					'sign_psbt',
					classifyWalletError(error) === 'user_rejected' ? 'cancelled' : 'failure',
					failure(error)
				)
				throw error
			}
			finishWalletFlow(signFlow, 'sign_psbt', 'success', context)

			const broadcastFlow = beginWalletFlow('utxo_split', provider, 'utxo_manager', 'broadcast', context)
			let broadcastData: any
			try {
				;({ data: broadcastData } = await axios.post(`${API_URL}/wallet/broadcast`, { rawtx }))
			} catch (error) {
				finishWalletFlow(broadcastFlow, 'broadcast', 'failure', failure(error))
				throw error
			}

			if (broadcastData.error) {
				finishWalletFlow(broadcastFlow, 'broadcast', 'failure', {
					...failure(new Error(String(broadcastData.error.message || 'broadcast rejected'))),
					error_category: 'invalid_response'
				})
				toast({
					title: 'Failed to broadcast',
					description: friendlyTxError(broadcastData.error.message)
				})
			} else {
				finishWalletFlow(broadcastFlow, 'broadcast', 'success', context)
				// A split the wallet page's prompt started: close the loop.
				const promptAddress = takeSplitPromptSource()
				if (promptAddress) {
					recordSplitPrompt('split_broadcast', promptAddress, {}, {
						wallet: String(Wallet.type() || ''),
						txid: String(broadcastData.result || '')
					})
				}
				toast({
					title: 'UTXOs created successfully',
					description: `Split into ${numOutputs} outputs`,
					action: (
						<ToastAction
							altText="View Transaction"
							onClick={() => window.open(`${EXPLORER_URL}/tx/${broadcastData.result}`, '_blank')}
						>
							View Transaction
						</ToastAction>
					)
				})
			}

			onClose()
		},
		[numOutputs, onClose, toast]
	)

	const invoiceItems = React.useMemo(() => {
		return [
			{
				title: 'Output',
				valueFunc: ({ building, psbt }: any) => {
					if (building || !psbt) return '--'
					return `${formatSatoshis(computedOutputValue)} ${NETWORK} / $${formatNumber(
						((computedOutputValue / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{
				title: 'Total',
				valueFunc: ({ building, fee, psbt }: any) => {
					if (building || !psbt) return '--'
					const totalFee = Number(fee) + computedServiceFee
					return `${formatSatoshis(totalFee)} ${NETWORK} / $${formatNumber(
						((totalFee / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{
				title: 'Service Fee',
				valueFunc: ({ building, psbt }: any) => {
					if (building || !psbt) return '--'
					return `${formatSatoshis(computedServiceFee)} ${NETWORK} / $${formatNumber(
						((computedServiceFee / 1e8) * exchangeRate)?.toFixed(2)
					)}`
				}
			},
			{ title: 'Network Fee' }
		]
	}, [exchangeRate, computedServiceFee, computedOutputValue])

	const buildDeps = React.useMemo(
		() => [selected, numOutputs, sendToAddress],
		[selected, numOutputs, sendToAddress]
	)

	return (
		<div className={embedded ? 'px-3 py-3 sm:px-4' : undefined}>
			{step === 1 && (
				<>
					{/* UTXO Selection */}
					{!embedded && (
						<p className="text-xs text-muted-foreground px-2 mb-2">
							Split large UTXOs into smaller ones to make multiple purchases at once, or consolidate
							small UTXOs into fewer, larger ones.
						</p>
					)}
					{utxos.length > 0 && (
						<div className="flex items-center gap-2 px-2 mb-2">
							<span className="text-[10px] text-muted-foreground shrink-0">
								{selected.size}/{utxos.length}
							</span>
							<div className="flex-1">
								<Slider
									defaultValue={[slider]}
									value={[slider]}
									max={utxos.length}
									step={1}
									onValueChange={onSliderChange}
									className="[&_[role=slider]]:size-3.5"
								/>
							</div>
							<div className="relative shrink-0" ref={sortRef}>
								<button
									onClick={() => setSortOpen(!sortOpen)}
									className="flex items-center gap-1 h-7 px-2 text-xs text-muted-foreground bg-transparent border border-border rounded-md hover:border-border transition-colors"
								>
									{sortOrder === 'biggest' ? 'Biggest' : 'Smallest'}
									<ChevronDown
										className={classNames('size-3 transition-transform', sortOpen && 'rotate-180')}
									/>
								</button>
								{sortOpen && (
									<div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-md shadow-lg overflow-hidden min-w-[120px]">
										<button
											onClick={() => handleSortChange('biggest')}
											className={classNames(
												'w-full text-left px-3 py-1.5 text-xs transition-colors',
												sortOrder === 'biggest'
													? 'text-foreground bg-accent'
													: 'text-muted-foreground hover:bg-accent hover:text-foreground'
											)}
										>
											Biggest first
										</button>
										<button
											onClick={() => handleSortChange('smallest')}
											className={classNames(
												'w-full text-left px-3 py-1.5 text-xs transition-colors',
												sortOrder === 'smallest'
													? 'text-foreground bg-accent'
													: 'text-muted-foreground hover:bg-accent hover:text-foreground'
											)}
										>
											Smallest first
										</button>
									</div>
								)}
							</div>
						</div>
					)}

					{isLoading && (
						<div className="flex justify-center py-8">
							<div className="size-4 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
						</div>
					)}

					{/* Change from a purchase the private relay still holds is counted in the
					    balance but cannot be spent (or split) until that purchase mines. */}
					{!isLoading && Number(data?.private_pending?.incoming) > 0 && (
						<p className="mx-2 mb-2 rounded-lg border border-[#9E77ED]/30 bg-[#9E77ED]/[0.07] px-3 py-2 text-[11px] leading-relaxed text-[#B692F6]">
							{formatSatoshis(Number(data.private_pending.incoming))} BTC is still settling through
							the private relay and will appear here once that purchase is mined.
						</p>
					)}

					{!isLoading && utxos.length === 0 && (
						<div className="flex justify-center py-8">
							<span className="text-xs text-muted-foreground">No UTXOs found</span>
						</div>
					)}

					{utxos.length > 0 && (
						<div className="max-h-[300px] overflow-y-auto mx-2 rounded-lg border border-border">
							{utxos.map((utxo: any) => {
								const isSelected = selected.has(utxo.outpoint)
								const usd = exchangeRate ? ((utxo.value / 1e8) * exchangeRate).toFixed(2) : '0'
								return (
									<div
										key={utxo.outpoint}
										onClick={() => handleSelect(utxo.outpoint)}
										className={classNames(
											'flex items-center gap-3 px-3 py-2.5 transition-colors border-b border-border/50 last:border-b-0',
											utxo.pending
												? 'cursor-default opacity-80'
												: isSelected
												? 'cursor-pointer bg-violet-500/10'
												: 'cursor-pointer hover:bg-accent/50'
										)}
										role="button"
										aria-disabled={utxo.pending || undefined}
										tabIndex={0}
										onKeyDown={onKeyboardClick(() => handleSelect(utxo.outpoint))}
									>
										{utxo.pending ? (
											<Clock className="size-5 stroke-amber-500 shrink-0" aria-hidden />
										) : isSelected ? (
											<CircleCheck className="size-5 stroke-violet-500 shrink-0" />
										) : (
											<CirclePlus className="size-5 stroke-zinc-500 shrink-0" />
										)}
										{/* Amount and its dollar value read as one figure: BTC leads,
										    USD sits right beside it in a quieter weight. */}
										<div className="flex min-w-0 items-baseline gap-2">
											<span className="inline-flex items-center gap-1.5 text-base font-semibold tabular-nums text-foreground">
												<BitcoinIcon className="size-4 text-orange-500" />
												{formatSatoshis(utxo.value)}
											</span>
											<span className="text-sm tabular-nums text-muted-foreground">${usd}</span>
										</div>
										{utxo.pending && (
											<span className="ml-auto shrink-0 text-xs font-medium text-amber-500">
												Pending
											</span>
										)}
									</div>
								)
							})}
						</div>
					)}

					{/* Selected total */}
					{utxos.length > 0 && selected.size > 0 && (
						<div className="mx-2 mt-2 rounded-lg border border-border">
							<div className="flex items-center justify-between px-3 py-2">
								<span className="text-xs text-muted-foreground">
									{selected.size} UTXO{selected.size !== 1 ? 's' : ''} selected
								</span>
								<div className="flex items-center gap-1.5">
									<BitcoinIcon className="size-3 text-orange-500" />
									<span className="text-xs font-medium text-foreground">
										{formatSatoshis(inputSats)}
									</span>
									<span className="text-[10px] text-muted-foreground">
										(${exchangeRate ? ((inputSats / 1e8) * exchangeRate).toFixed(2) : '0'})
									</span>
								</div>
							</div>
						</div>
					)}

					{/* Next button */}
					{utxos.length > 0 && (
						<div className="px-2 mt-3 mb-3">
							<button
								disabled={selected.size === 0}
								onClick={() => setStep(2)}
								className="w-full h-10 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
							>
								Next
							</button>
							<p className="text-xxs text-muted-foreground select-none mt-2">
								By clicking the button above you agree to the
								<Link target="_blank" href="/terms" className="ml-1 underline">
									Terms of Service
								</Link>
								.
							</p>
						</div>
					)}
				</>
			)}

			{step === 2 && (
				<>
					{/* Selected Inputs */}
					<div className="mx-2 mb-3 rounded-lg border border-border">
						<div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
							<span className="text-xs font-semibold text-muted-foreground">Selected Inputs</span>
							<button
								onClick={() => setStep(1)}
								className="text-violet-700 dark:text-violet-400 hover:text-violet-700 dark:text-violet-300 transition-colors"
							>
								<EditIcon className="size-4" />
							</button>
						</div>
						<div className="max-h-[150px] overflow-y-auto">
							{selectedUtxos.map((e) => {
								const usd = exchangeRate ? ((e.value / 1e8) * exchangeRate).toFixed(2) : '0'
								return (
									<div
										key={e.outpoint}
										className="flex items-center justify-between px-3 py-2 border-b border-border/50 last:border-b-0"
									>
										<div className="flex items-baseline gap-2">
											<span className="inline-flex items-center gap-1.5 text-base font-semibold tabular-nums text-foreground">
												<BitcoinIcon className="size-4 text-orange-500" />
												{formatSatoshis(e.value)}
											</span>
											<span className="text-sm tabular-nums text-muted-foreground">${usd}</span>
										</div>
									</div>
								)
							})}
						</div>
					</div>

					{/* Output config */}
					<div className="flex flex-col gap-2 px-2 mb-3">
						<div className="flex items-center gap-2">
							<span className="text-xs text-muted-foreground shrink-0">Outputs</span>
							<div className="flex-1">
								<Slider
									defaultValue={[numOutputs]}
									value={[Math.min(numOutputs, 10)]}
									min={1}
									max={10}
									step={1}
									onValueChange={([v]) => setNumOutputs(v)}
									className="[&_[role=slider]]:size-3.5"
								/>
							</div>
							<input
								type="number"
								min={1}
								value={numOutputs}
								onChange={handleOutputsInput}
								className="w-12 h-7 text-center text-xs font-medium text-foreground bg-background border border-border rounded focus:outline-none focus:border-border transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
							/>
						</div>
					</div>

					{/* What you get: the builder splits the output value evenly and
					    gives the first output the rounding remainder (plan-split.ts);
					    mirror that here so the table is what lands on-chain. */}
					<div className="mx-2 mb-3 rounded-lg border border-border">
						<div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
							<span className="text-xs font-semibold text-muted-foreground">
								You will receive {numOutputs} UTXO{numOutputs !== 1 ? 's' : ''}
							</span>
							<span className="text-xs text-muted-foreground">back to your payment address</span>
						</div>
						<div className="max-h-[220px] overflow-y-auto">
							{Array.from({ length: Math.max(1, numOutputs) }, (_, i) => {
								const total = computedOutputValue
								const per = total > 0 ? Math.floor(total / numOutputs) : 0
								const value = per + (i === 0 ? total - per * numOutputs : 0)
								const usd = exchangeRate && value ? ((value / 1e8) * exchangeRate).toFixed(2) : null
								return (
									<div
										key={i}
										className="flex items-center justify-between px-3 py-2 border-b border-border/50 last:border-b-0"
									>
										<span className="text-sm text-muted-foreground tabular-nums">#{i + 1}</span>
										<div className="flex items-baseline gap-2">
											<span className="inline-flex items-center gap-1.5 text-base font-semibold tabular-nums text-foreground">
												<BitcoinIcon className="size-4 text-orange-500" />
												{value > 0 ? formatSatoshis(value) : '—'}
											</span>
											<span className="text-sm tabular-nums text-muted-foreground">
												{usd ? `$${usd}` : ''}
											</span>
										</div>
									</div>
								)
							})}
						</div>
					</div>

					{/* Invoice */}
					<Invoice
						open={selected.size > 0}
						onBuild={handleBuildPsbt}
						buildDeps={buildDeps}
						onSubmit={handleSubmit}
						action={({ building }) => {
							if (building) return 'Calculating...'
							return `Create ${numOutputs} UTXO${numOutputs !== 1 ? 's' : ''}`
						}}
						items={invoiceItems}
						inline={embedded}
					/>
				</>
			)}
		</div>
	)
}

export default UtxoManager
